import { api, ApiError } from './api-client';
import { getApiBaseUrl } from './config';

export interface User {
  id: string;
  name: string;
  email: string;
  avatar: string;
  plan: 'free' | 'starter' | 'team' | 'enterprise';
  createdAt: string;
}

/**
 * Auth store that talks to the PostgreSQL-backed API.
 * Falls back to localStorage simulation if the API is unreachable.
 */

let _apiAvailable: boolean | null = null;

async function isApiAvailable(): Promise<boolean> {
  if (_apiAvailable !== null) return _apiAvailable;
  try {
    const res = await fetch(getApiBaseUrl() + '/api/health', { signal: AbortSignal.timeout(2000) });
    _apiAvailable = res.ok;
  } catch {
    _apiAvailable = false;
  }
  return _apiAvailable;
}

// ---------- Local fallback (original localStorage logic) ----------

const USERS_KEY = 'rynora_users';
const SESSION_KEY = 'rynora_session';
const RESET_TOKENS_KEY = 'rynora_reset_tokens';

function getLocalUsers(): Record<string, { user: User; passwordHash: string }> {
  try {
    return JSON.parse(localStorage.getItem(USERS_KEY) || '{}');
  } catch {
    return {};
  }
}
function saveLocalUsers(users: Record<string, { user: User; passwordHash: string }>) {
  localStorage.setItem(USERS_KEY, JSON.stringify(users));
}
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return 'h_' + Math.abs(hash).toString(36);
}
function generateAvatar(name: string): string {
  return (
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '?'
  );
}

// ---------- Public API ----------

export async function register(
  name: string,
  email: string,
  password: string,
): Promise<{ success: boolean; error?: string; user?: User }> {
  if (await isApiAvailable()) {
    try {
      const data = await api.auth.register(name, email, password);
      return { success: true, user: data.user as User };
    } catch (err) {
      return { success: false, error: err instanceof ApiError ? err.message : 'Registration failed.' };
    }
  }

  // Local fallback
  const users = getLocalUsers();
  const normalizedEmail = email.toLowerCase().trim();
  if (users[normalizedEmail]) return { success: false, error: 'An account with this email already exists.' };
  if (password.length < 8) return { success: false, error: 'Password must be at least 8 characters.' };

  const user: User = {
    id: crypto.randomUUID(),
    name: name.trim(),
    email: normalizedEmail,
    avatar: generateAvatar(name),
    plan: 'free',
    createdAt: new Date().toISOString(),
  };
  users[normalizedEmail] = { user, passwordHash: simpleHash(password) };
  saveLocalUsers(users);
  setSession(user);
  return { success: true, user };
}

export async function login(
  email: string,
  password: string,
): Promise<{ success: boolean; error?: string; user?: User }> {
  if (await isApiAvailable()) {
    try {
      const data = await api.auth.login(email, password);
      return { success: true, user: data.user as User };
    } catch (err) {
      return { success: false, error: err instanceof ApiError ? err.message : 'Login failed.' };
    }
  }

  // Local fallback
  const users = getLocalUsers();
  const record = users[email.toLowerCase().trim()];
  if (!record) return { success: false, error: 'No account found with this email address.' };
  if (record.passwordHash !== simpleHash(password)) return { success: false, error: 'Incorrect password.' };
  setSession(record.user);
  return { success: true, user: record.user };
}

export async function requestPasswordReset(
  email: string,
): Promise<{ success: boolean; error?: string; token?: string }> {
  if (await isApiAvailable()) {
    try {
      const data = await api.auth.forgotPassword(email);
      return { success: true, token: data._demo_token };
    } catch (err) {
      return { success: false, error: err instanceof ApiError ? err.message : 'Something went wrong.' };
    }
  }

  // Local fallback
  const users = getLocalUsers();
  const normalizedEmail = email.toLowerCase().trim();
  if (!users[normalizedEmail]) return { success: true };
  const token = crypto.randomUUID().slice(0, 8);
  const tokens: Record<string, { email: string; expiresAt: number }> = JSON.parse(
    localStorage.getItem(RESET_TOKENS_KEY) || '{}',
  );
  tokens[token] = { email: normalizedEmail, expiresAt: Date.now() + 3600000 };
  localStorage.setItem(RESET_TOKENS_KEY, JSON.stringify(tokens));
  return { success: true, token };
}

export async function resetPassword(token: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
  if (await isApiAvailable()) {
    try {
      await api.auth.resetPassword(token, newPassword);
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof ApiError ? err.message : 'Reset failed.' };
    }
  }

  // Local fallback
  const tokens: Record<string, { email: string; expiresAt: number }> = JSON.parse(
    localStorage.getItem(RESET_TOKENS_KEY) || '{}',
  );
  const record = tokens[token];
  if (!record) return { success: false, error: 'Invalid or expired reset link.' };
  if (Date.now() > record.expiresAt) {
    delete tokens[token];
    localStorage.setItem(RESET_TOKENS_KEY, JSON.stringify(tokens));
    return { success: false, error: 'Reset link has expired.' };
  }
  if (newPassword.length < 8) return { success: false, error: 'Password must be at least 8 characters.' };
  const users = getLocalUsers();
  if (users[record.email]) {
    users[record.email].passwordHash = simpleHash(newPassword);
    saveLocalUsers(users);
  }
  delete tokens[token];
  localStorage.setItem(RESET_TOKENS_KEY, JSON.stringify(tokens));
  return { success: true };
}

export function setSession(user: User) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(user));
}

export function getSession(): User | null {
  // Try API-cached user first
  const cached = api.auth.getCachedUser();
  if (cached) return cached as User;
  // Local fallback
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function logout() {
  api.auth.logout();
  localStorage.removeItem(SESSION_KEY);
}
